import { datadogRum } from '@datadog/browser-rum';
import { Grid, Theme, useTheme } from '@mui/material';
import { cloneDeep as _cloneDeep, get as _get } from 'lodash';
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  converseBack,
  converseEdit,
  converseNext,
  converseSubmit,
  createSafelite,
  deleteDraft,
} from '../../../api/converseApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { useModalVisibility } from '../../../app/hooks/useModalVisibility';
import {
  setErsClaim,
  setGlassClaim,
  updateReporterContactInfo,
} from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import { deleteDraftClaim } from '../../../app/store/Claims/ClaimsSlice';
import {
  getAuthKey,
  getClaimRisks,
  getMobileSourceSystem,
  getPartnerId,
  getQuestionSetPartner,
  getSelectedRiskPolicyNumber,
  getServicingOpCo,
  getSessionId,
  getUrlKey,
  getUserData,
  userIsThirdParty,
} from '../../../app/store/combinedSelectors';
import { updateAuthToken } from '../../../app/store/ContextRouter/ContextRouterActions';
import { store } from '../../../app/store/store';
import {
  backBtn,
  cancelBtn,
  continueBtn,
  CONVERSE_DRAFT_ALREADY_SUBMITTED_CODE,
  submitClaimBtn,
} from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import getCopyById from '../../../copy/copyMapper';
import { FnolQuestionTypes, SubmitQuestionStoreModel } from '../../../model/create-draft-claim-request';
import {
  buildDraftClaimPayload,
  createGlassPayload,
  createSubmitQuestionPayload,
} from '../../../services/SetupFnol.service';
import { setupPostFnol } from '../../../services/SetupPostFnol.service';
import { DamagePickerData, DamageTypes, GenericProductType } from '../../../types/type';
import { getPolicySource } from '../../../utils/PolicySource-Util';
import { postMessageToParentApp } from '../../../utils/Utilities';
import DamagePickerForm from '../../post-fnol-auto/DamagePicker/DamagePickerForm';
import MultiSelect, { MultiSelectStyleInfo } from '../../post-fnol-auto/MultiSelect/MultiSelect';
import SingleSelect, { SingleSelectStyleInfo } from '../../post-fnol-auto/SingleSelect/SingleSelect';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ContactInfo from '../../shared/ContactInfo/ContactInfo';
import ErrorBanner from '../../shared/ErrorBanner/ErrorBanner';
import FreeForm from '../../shared/FreeformText/Freeform';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import ProgressBar from '../../shared/ProgressBar/ProgressBar';
import ThirdPartyFNOLContact from '../../third-party-fnol/ThirdPartyFNOLContactForm/ThirdPartyFNOLContactFormWrapper';
import ReviewFnol from '../review-fnol/ReviewFnol';
import ThirdPartyInfo from '../third-party-info/ThirdPartyInfo';

/**
 * Waits for claim details to be populated in Redux state
 * Polls the Redux store to verify the claim number is loaded and is not a draft number
 * @param expectedClaimNumber - The claim number we expect to see in Redux
 * @param timeoutMs - Maximum time to wait in milliseconds
 * @returns Promise<boolean> - true if claim details loaded successfully, false if timeout
 */
function waitForClaimDetails(expectedClaimNumber: string, timeoutMs = 10000): Promise<boolean> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    const pollInterval = 200; // Check every 200ms

    const checkClaimDetails = () => {
      const state = store.getState();
      const claimNumberInRedux = _get(state, 'claimDetail.claimSummary.claimNumber', '');
      const claimResponse = _get(state, 'claimDetail.claimSummary', null);

      console.log('🔍 Polling Redux for claim details:', {
        expectedClaimNumber: expectedClaimNumber,
        claimNumberInRedux: claimNumberInRedux,
        hasClaimResponse: !!claimResponse,
        elapsed: Date.now() - startTime,
      });

      // Check if claim details are loaded and claim number is set
      if (claimResponse && claimNumberInRedux) {
        // Clean the claim numbers for comparison (remove dashes)
        const cleanExpected = expectedClaimNumber.replace(/-/g, '');
        const cleanActual = claimNumberInRedux.replace(/-/g, '');

        if (cleanActual === cleanExpected) {
          console.log('✅ Claim details loaded in Redux successfully');
          resolve(true);
          return;
        }
      }

      // Check if we've exceeded the timeout
      if (Date.now() - startTime >= timeoutMs) {
        console.error('❌ Timeout waiting for claim details in Redux', {
          timeoutMs,
          claimNumberInRedux,
          expectedClaimNumber,
        });
        resolve(false);
        return;
      }

      // Continue polling
      setTimeout(checkClaimDetails, pollInterval);
    };

    // Start polling
    checkClaimDetails();
  });
}

export default function DynamicQuestion(props: any) {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const opcoQuestionSet = useSelector(getQuestionSetPartner);
  const partnerOpCo = useSelector(getServicingOpCo);
  const opco = opcoQuestionSet !== '' || undefined ? opcoQuestionSet : partnerOpCo;
  const partnerId = useSelector(getPartnerId);
  const copy = getCopyById(opco, partnerId);
  const isThirdParty = useSelector(userIsThirdParty);
  const policies = useAppSelector((state) => state.policies.policies);
  const selectedPolicyNumber = useSelector(getSelectedRiskPolicyNumber);

  const { state } = useLocation();
  const [activeQuestion, setActiveQuestion] = React.useState(_get(state, 'activeQuestion.question'));
  const [currentAnswered, setCurrentAnswered] = React.useState<number>(1);
  const [formattedAnswers, setFormattedAnswers] = React.useState<any>();
  const [selectedAnswer, setSelectedAnswer] = React.useState('');
  const [complexAnswer, setComplexAnswer] = React.useState<any>(null);
  const [prefillComplexValue, setPrefillComplexValue] = React.useState<any>();
  const [reviewSheet, setReviewSheet] = React.useState<any>(_get(state, 'reviewSheet'));
  const [nextEnabled, setNextEnabled] = React.useState(
    !!complexAnswer || !!selectedAnswer || _get(activeQuestion, 'questionType') === FnolQuestionTypes.END
  );
  const [showErrorBanner, setShowErrorBanner] = React.useState(false);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const customerData = useSelector(getUserData);
  const mobileSourceSystem = useSelector(getMobileSourceSystem);
  const claimInfo = useAppSelector((state) => state.claimCreation);
  const claimRiskInfo = useSelector(getClaimRisks);
  const isAuto = _get(state, 'isAuto');
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Calls the custom hook useModalVisibility, passing in the setModalLoadingOpen function.The hook returns a new function, setModalVisibility, which you can use to show or hide the modal.
  const setModalVisibility = useModalVisibility(setModalLoadingOpen);
  const themeBasedStyleInfo: SingleSelectStyleInfo = {
    headerStyle: theme.custom.styles.titleType4,
    descriptionStyle: theme.custom.styles.primaryDescription,
    optionStyle: {
      ...theme.custom.styles.primaryDescription,
      radioButtonColor: theme.custom.colors.radioButtonColor,
      radioButtonSelectedColor: theme.custom.colors.radioButtonSelectedColor,
      borderColor: theme.custom.styles.showBorderAroundListItems
        ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
        : theme.custom.colors.bodyColor,
      selectedBorderColor: `${theme.custom.styles.listItemBorderSelectedStyle} ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`,
      borderRadius: theme.custom.styles.borderRadius.radiusDefault,
      radioButtonSize: theme.custom.styles.defaultRadioButtonSize,
      optionHeight: theme.custom.styles.defaultInputHeight,
    },
  };

  const checkboxStyleFromTheme: MultiSelectStyleInfo = {
    headerStyle: theme.custom.styles.titleType4,
    descriptionStyle: theme.custom.styles.primaryDescription,
    optionStyle: {
      ...theme.custom.styles.primaryDescription,
      checkBoxBorderColor: theme.custom.styles.checkBox.defaultBorder,
      checkBoxSelectedColor: theme.custom.colors.radioButtonSelectedColor,
      borderColor: theme.custom.styles.showBorderAroundListItems
        ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
        : theme.custom.colors.bodyColor,
      selectedBorderColor: `${theme.custom.styles.listItemBorderSelectedStyle} ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`,
      borderRadius: theme.custom.styles.borderRadius.radiusDefault,
      checkBoxSize: theme.custom.styles.defaultCheckBoxSize,
      optionHeight: theme.custom.styles.defaultInputHeight,
    },
  };

  // DynamicQuestion stays mounted on a single route ('/dynamic-question') for the
  // entire question flow, including the terminal review step - activeQuestion changes
  // via internal state, not React Router navigation. The app-wide scroll-to-top
  // mechanism (ScrollMessageWrapper) only re-arms on location.pathname changes, so it
  // never fires for these in-place transitions, and this window's own scrollTop is
  // otherwise never reset. On a short question page the user may have scrolled down to
  // reach Continue; carrying that offset into the much taller review screen pushes its
  // heading and top sections out of view. Reset locally whenever the active step changes.
  //
  // Blurring matters just as much as the scroll reset: the Continue/Submit button in
  // CallToAction keeps browser focus across this transition (same DOM node, just a text
  // change), several iframes deep (MyAccount > bridge app > this app). As iframe-resizer
  // keeps growing the outer <iframe> to match this page's new height, the browser's native
  // "keep the focused control in view" behavior re-scrolls MyAccount's own window to reveal
  // that now deeply-buried button - undoing MyAccount's scrollIntoView(#app-wrapper) and
  // fighting every timing-based fix that came before, since none of them touch focus.
  //
  // ModalLinearActivityIndicator (shown via setModalVisibility while this step's API call
  // is in flight) is a MUI Modal, which restores focus to whatever was focused when it
  // opened - this same Continue/Submit button - once it unmounts. That unmount happens
  // after setActiveQuestion in showDynamicQuestions/handleBack/handleEdit, asynchronously
  // re-focusing the button after this effect's immediate blur() already ran. Fixed at the
  // source with disableRestoreFocus on that shared modal; the deferred blur here is a
  // second line of defense against any other focus-restoring code in this transition.
  useEffect(() => {
    window.scrollTo(0, 0);
    const blurActiveElement = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    blurActiveElement();
    const timeoutId = setTimeout(blurActiveElement, 0);
    return () => clearTimeout(timeoutId);
  }, [activeQuestion]);

  useEffect(() => {
    if (activeQuestion && activeQuestion.answers) {
      const updatedAnswers = activeQuestion.answers.map((answer: any) => {
        return {
          isSelected: !!answer.selected,
          text: answer.displayText,
          id: answer.answerId,
        };
      });

      setFormattedAnswers(updatedAnswers);
    } else {
      setFormattedAnswers(null);
    }
  }, [activeQuestion]);

  async function handleNext() {
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    // Reset complex answer info
    setComplexAnswer(null);
    if (_get(activeQuestion, 'questionType') === FnolQuestionTypes.END) {
      submitClaimAndRedirect();
    } else {
      showDynamicQuestions();
    }
  }

  async function handleBack() {
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setModalVisibility(true);
    // Reset answer state to prevent stale data from previous question
    setSelectedAnswer('');
    setComplexAnswer(null);
    const lob = isAuto ? 'auto' : 'property';
    const claimNumber = _get(state, 'draftClaimNumber', '').replace(/-/g, '');

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isAuto) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    const converseBackResponse = await converseBack(
      urlKey,
      sessionId,
      authKey,
      lob,
      opco,
      claimNumber,
      activeQuestion.id,
      policySource
    );
    setModalVisibility(false);
    if (_get(converseBackResponse, 'status.statusCode', '') >= 200) {
      dispatch(updateAuthToken(converseBackResponse.data.updatedAuthToken));
      const question = converseBackResponse.data.response.question;
      // Set activeQuestion first to trigger re-render
      setActiveQuestion(question);
      // Then prefill the answer data
      prefillQuestionAnswer(question);
      setNextEnabled(true);
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_BACK_ERROR, claimNumber, converseBackResponse);
      navigate('/error-banner', {
        state: {
          claimNumber,
        },
      });
    }
  }

  function handleSingleSelectionChange(answer: string) {
    setSelectedAnswer(answer);
    setNextEnabled(!!answer);
  }

  function handleMultiSelectChange(answers: string[]) {
    let combinedAnswer = '';
    answers.forEach((answer: string) => (combinedAnswer += answer + ','));
    combinedAnswer = combinedAnswer.substring(0, combinedAnswer.length - 1);
    setSelectedAnswer(combinedAnswer);
    setNextEnabled(!!combinedAnswer);
  }

  function handleFreeformChange(answer: string) {
    setSelectedAnswer(answer);
    setNextEnabled(!!answer);
  }

  function handleContactTypeChange(answer: any) {
    setComplexAnswer(answer);
    setNextEnabled(!!answer.isPhoneValid && !!answer.isEmailValid);
  }

  const handleDamagePickerDataUpdate = useCallback((updatedData: DamagePickerData) => {
    let cleansedData = {};
    if (
      updatedData.selectedDamageTypeOption === DamageTypes.NO_DAMAGE ||
      updatedData.selectedDamageTypeOption === DamageTypes.TOTAL_LOSS
    ) {
      cleansedData = {
        ...updatedData,
        damageDescription: '',
        selectedDamageCodes: [],
      };
    } else {
      cleansedData = { ...updatedData };
    }
    setComplexAnswer(cleansedData);
    setNextEnabled(!!updatedData.selectedDamageTypeOption);
  }, []);

  const handleThirdPartyContactChange = useCallback((answer: any) => {
    setComplexAnswer(answer);
    let enableNext = true;
    if (Array.isArray(answer)) {
      answer.forEach((contact: any) => {
        if (!contact.phoneNumberValid || !contact.emailAddressValid) {
          enableNext = false;
        }
      });
      setNextEnabled(enableNext);
      setPrefillComplexValue(answer);
    }
  }, []);

  function handleClaimantChange(answer: any) {
    setComplexAnswer(_cloneDeep(answer));
    setNextEnabled(!!answer);
  }

  function prefillQuestionAnswer(question: any) {
    switch (_get(question, 'questionType')) {
      case FnolQuestionTypes.FREE_FORM: {
        const responseValue = _get(question, 'answers[0].response', '');
        setSelectedAnswer(responseValue);
        break;
      }
      case FnolQuestionTypes.VEHICLE_DAMAGE_PICKER:
        setPrefillComplexValue({
          damageDescription: _get(question, 'answers[0].answerData.vehDamageDamageDescription', ''),
          selectedDamageTypeOption: _get(question, 'answers[0].answerData.damageType', ''),
          selectedDamageCodes: _get(question, 'answers[0].answerData.vehicleDamages', ''),
        });
        break;
      case FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM: {
        const thirdPartyInfo = _get(question, 'answers[0].answerData.thirdPartyContactForm', []);
        setPrefillComplexValue(
          thirdPartyInfo.map((thirdParty: any, index: number) => {
            return {
              id: index,
              firstName: _get(thirdParty, 'claimContact.contact.firstName', ''),
              lastName: _get(thirdParty, 'claimContact.contact.lastName', ''),
              personInjured: _get(thirdParty, 'isThirdPartyInjured', '').length ? true : false,
              phoneNumber:
                _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.area', '') +
                ' ' +
                _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.number', ''),
              phoneNumberValid: true,
              emailAddress: _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.emailAddress', ''),
              emailAddressValid: true,
              vehicleInvolved: !!_get(thirdParty, 'vehicleDetail'),
              vehicleYear: _get(thirdParty, 'vehicleDetail.vehTypeYear', ''),
              vehicleMake: _get(thirdParty, 'vehicleDetail.vehTypeMake', ''),
              vehicleModel: _get(thirdParty, 'vehicleDetail.vehTypeModel', ''),
              addressLine1: _get(thirdParty, 'claimContact.contact.newAddress[0].addressLine1', ''),
              addressLine2: _get(thirdParty, 'claimContact.contact.newAddress[0].addressLine2', ''),
              city: _get(thirdParty, 'claimContact.contact.newAddress[0].city', ''),
              state: _get(thirdParty, 'claimContact.contact.newAddress[0].state', ''),
              zipCode: _get(thirdParty, 'claimContact.contact.newAddress[0].zip', ''),
            };
          })
        );
        break;
      }
      case FnolQuestionTypes.INSURED_CONTACT_TYPE:
        setPrefillComplexValue({
          phoneNumber:
            _get(question, 'answers[0].answerData.areaCode', '') +
            _get(question, 'answers[0].answerData.phoneNumber', ''),
          emailAddress: _get(question, 'answers[0].answerData.email', ''),
        });
        break;

      default:
        setSelectedAnswer('');
        setPrefillComplexValue(null);
    }
  }

  async function handleEdit(row: any) {
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    setModalVisibility(true);
    // Reset answer state to prevent stale data from previous question
    setSelectedAnswer('');
    setComplexAnswer(null);
    const lob = isAuto ? 'auto' : 'property';
    const claimNumber = _get(state, 'draftClaimNumber', '').replace(/-/g, '');

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isAuto) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    const converseEditResponse = await converseEdit(
      urlKey,
      sessionId,
      authKey,
      lob,
      opco,
      claimNumber,
      row.questionId,
      policySource
    );
    setModalVisibility(false);
    if (_get(converseEditResponse, 'status.statusCode', '') >= 200) {
      dispatch(updateAuthToken(converseEditResponse.data.updatedAuthToken));
      prefillQuestionAnswer(converseEditResponse.data.response.question);
      setActiveQuestion(converseEditResponse.data.response.question);
      setNextEnabled(true);
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_EDIT_ERROR, claimNumber, converseEditResponse);
      navigate('/error-banner', {
        state: {
          claimNumber,
        },
      });
    }
  }

  async function handleCancel() {
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
    if (isThirdParty) {
      navigate('/third-party-guest-type');
    } else {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    }
  }

  /**
   * Loads post-FNOL data for a submitted claim and navigates to its confirmation page.
   * Used after a successful submit, and when Converse reports the draft was already
   * submitted (E1PCDC-7008).
   */
  async function routeToSubmittedClaim(submittedClaimNumber: string, authToken: string) {
    const policyRiskId: string = _get(claimRiskInfo, 'policy.risks.id', '');
    const successfulSetup = await setupPostFnol(
      customerData,
      submittedClaimNumber,
      urlKey,
      sessionId,
      authToken,
      policyRiskId !== '-1',
      opco,
      'post-submission'
    );
    if (successfulSetup === GenericProductType.Auto || successfulSetup === GenericProductType.Home) {
      // Wait for claim details to be populated in Redux before navigating
      console.log('⏳ Waiting for claim details to be loaded in Redux before navigating to confirmation...');
      const claimDetailsLoaded = await waitForClaimDetails(submittedClaimNumber, 10000); // 10 second timeout

      if (claimDetailsLoaded) {
        console.log('✅ Claim details verified in Redux, navigating to confirmation');
        datadogRum.setGlobalContextProperty('claimDetailsLoadedBeforeConfirmation', true);
      } else {
        console.error('⚠️ Claim details not loaded in Redux after timeout, navigating anyway');
        // Tag this session in Datadog so we can query for instances where this issue occurs
        datadogRum.setGlobalContextProperty('claimDetailsLoadedBeforeConfirmation', false);
        datadogRum.setGlobalContextProperty('claimDetailsLoadTimeout', true);
        datadogRum.addAction('claim-details-load-timeout', {
          expectedClaimNumber: submittedClaimNumber,
          claimType: isAuto ? 'auto' : 'property',
          message: 'Claim details not loaded in Redux before navigating to confirmation page',
        });
      }

      if (isAuto) {
        navigate('/auto-claim-submission');
      } else {
        navigate('/property-claim-submission');
      }
    } else {
      navigate('/timeout-claim-submission');
    }
  }

  async function submitClaimAndRedirect() {
    if (isSubmitting) return;

    // GUARD: Prevent submission of glass/ERS claims (check both activeQuestion type and Redux state)
    if (
      activeQuestion?.questionType === FnolQuestionTypes.GLASS_END ||
      activeQuestion?.questionType === FnolQuestionTypes.ERS_END ||
      claimInfo.isGlassClaim ||
      claimInfo.isErsClaim
    ) {
      console.error('Attempted to submit glass/ERS claim - redirecting to call-us page');

      // Determine which page to redirect to
      if (activeQuestion?.questionType === FnolQuestionTypes.GLASS_END || claimInfo.isGlassClaim) {
        navigate('/glass-call-us');
      } else {
        navigate('/ers-call-us');
      }
      return;
    }

    setIsSubmitting(true);
    setModalVisibility(true);
    const lob = isAuto ? 'auto' : 'property';

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isAuto) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    const draftClaimNumber = _get(state, 'draftClaimNumber', '').replace(/-/g, '');
    let claimNumber = '';
    try {
      const converseResponse = await converseSubmit(
        urlKey,
        sessionId,
        authKey,
        lob,
        opco,
        draftClaimNumber,
        policySource
      );
      const claimNumberFromResponse = _get(converseResponse, 'data.response.claimNumber', '');
      if (claimNumberFromResponse.length) {
        claimNumber = claimNumberFromResponse;
      }
      if (isThirdParty) {
        navigate('/third-party-confirmation', {
          state: {
            claimNumber: _get(converseResponse, 'data.response.claimNumber', ''),
          },
        });
      } else if (converseResponse && claimNumber.length) {
        dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
        await routeToSubmittedClaim(claimNumberFromResponse, converseResponse.data.updatedAuthToken);
      } else if (converseResponse.statusCode) {
        if (converseResponse.statusCode === 504) {
          navigate('/timeout-claim-submission');
          postMessageToParentApp(
            PostMessageCodes.DIGITAL_CLAIM_V2_AUTO_CLAIM_SUBMIT_TIMEOUT_ERROR,
            draftClaimNumber,
            converseResponse
          );
        } else {
          postMessageToParentApp(
            PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_SUBMIT_ERROR,
            draftClaimNumber,
            converseResponse
          );
          navigate('/error-banner', {
            state: {
              draftClaimNumber,
            },
          });
        }
      }
    } catch (error) {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_SUBMIT_ERROR, draftClaimNumber);
      navigate('/error-banner', {
        state: {
          draftClaimNumber,
        },
      });
    } finally {
      setModalVisibility(false); // Always hide modal at the end
    }
  }

  async function showDynamicQuestions() {
    setModalVisibility(true);
    const draftClaimNumber = _get(state, 'draftClaimNumber', '').replace(/-/g, '');
    const questionModel: SubmitQuestionStoreModel = {
      questionId: activeQuestion.id,
      questionType: activeQuestion.questionType,
      draftClaimNumber: _get(state, 'draftClaimNumber', ''),
    };

    if (complexAnswer) {
      switch (activeQuestion.questionType) {
        case FnolQuestionTypes.INSURED_CONTACT_TYPE:
          questionModel.phoneNumber = complexAnswer.phone;
          questionModel.emailAddress = complexAnswer.email;
          dispatch(
            updateReporterContactInfo({
              contactInfo: {
                emailAddress: _get(questionModel, 'emailAddress', ''),
                phoneNumber: _get(questionModel, 'phoneNumber', ''),
              },
            })
          );
          break;
        case FnolQuestionTypes.VEHICLE_DAMAGE_PICKER:
          questionModel.vehicleDamagePicker = {
            damageType: complexAnswer.selectedDamageTypeOption,
            vehDamageDamageDescription: complexAnswer.damageDescription,
            vehicleDamages:
              complexAnswer.selectedDamageTypeOption === DamageTypes.PARTIAL_DAMAGE
                ? complexAnswer.selectedDamageCodes.map((part: string) => {
                    return {
                      vehicleDamage: part,
                    };
                  })
                : [],
          };
          break;
        case FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM:
          questionModel.thirdPartyContactForm = complexAnswer;
          questionModel.numberOfContacts = _get(claimInfo, 'draftPayload.claimContacts', []).length;
          break;
        case FnolQuestionTypes.CLAIMANT_CONTACT_TYPE: {
          const updatedDraftBody = buildDraftClaimPayload(
            customerData,
            claimInfo,
            claimInfo.dateLocationInfo,
            opco,
            mobileSourceSystem,
            {
              reportedBy: _get(claimInfo, 'draftPayload.claimContacts', []).length + 1,
              addReportedBy: true,
              addVehicleDetails: true,
              creatingDraft: false,
            }
          );
          questionModel.claimantContactForm = complexAnswer;
          questionModel.draftClaimInfo = updatedDraftBody;
          questionModel.numberOfContacts = _get(claimInfo, 'draftPayload.claimContacts', []).length + 1;
          break;
        }
        default:
          break;
      }
    }

    const nextQuestionPayload = createSubmitQuestionPayload(selectedAnswer, questionModel);

    const lob = isAuto ? 'auto' : 'property';

    // Send the policy-source header in requests to Converse to enable differentiation of question set retrieval between CAH AUTO and other OPCO lines of business (LOBs).
    let policySource = '';
    if (isAuto) {
      policySource = getPolicySource(selectedPolicyNumber, policies);
    }

    const converseResponse = await converseNext(
      urlKey,
      sessionId,
      authKey,
      lob,
      opco,
      _get(state, 'draftClaimNumber', '').replace(/-/g, ''),
      nextQuestionPayload,
      policySource
    );

    if (_get(converseResponse, 'status.statusCode', '') >= 200) {
      setActiveQuestion(null);
      if (_get(converseResponse, 'data.updatedAuthToken'))
        dispatch(updateAuthToken(converseResponse.data.updatedAuthToken));
      setSelectedAnswer('');
      setActiveQuestion(converseResponse.data.response.question);
      setNextEnabled(false);
      setCurrentAnswered(converseResponse.data.response.answered);
      if (_get(converseResponse, 'data.response.question.questionType') === FnolQuestionTypes.END) {
        setReviewSheet(_get(converseResponse, 'data.response.question.reviewSheet'));
        setNextEnabled(true);
      } else if (_get(converseResponse, 'data.response.question.questionType') === FnolQuestionTypes.ERS_END) {
        dispatch(setErsClaim({ isErsClaim: true }));
        await deleteDraftWithDispatch(converseResponse);
        navigate('/ers-call-us');
      } else if (_get(converseResponse, 'data.response.question.questionType') === FnolQuestionTypes.GLASS_END) {
        dispatch(setGlassClaim({ isGlassClaim: true }));
        if (!claimInfo.riskInfo.vin) {
          // We won't redirect to SafeLite in this scenario, so simply delete the draft claim and navigate to the glass-call-us page
          await deleteDraftWithDispatch(converseResponse);
          navigate('/glass-call-us');
        } else {
          // In this case, we want to redirect to SafeLite to complete the glass claim,
          // so first get the safelite token informaton to allow for this, and then delete the draft claim.
          const glassPayload = createGlassPayload(claimInfo, copy.privacyAndSecurityLink);

          const safelitePayload = await createSafelite(
            urlKey,
            sessionId,
            _get(converseResponse, 'data.updatedAuthToken', authKey),
            opco,
            glassPayload
          );

          // Before posting any messages related to the SafeLite response,
          // attempt to delete the draft claim
          const deleteResponse = await deleteDraftWithDispatch(converseResponse);

          if (deleteResponse && !(deleteResponse instanceof Error)) {
            // We successfully deleted the draft claim, so we can now post messages related to the SafeLite response
            if (safelitePayload) {
              window.parent.postMessage(
                {
                  messageName: PostMessageCodes.DIGITAL_CLAIM_V2_LAUNCH_SAFELITE,
                  htmlContent: safelitePayload,
                },
                '*'
              );
            } else {
              // Safelite payload retrieval failed
              setShowErrorBanner(true);
              // Cannot pass the erorrInfo object due to the different implementation in the network layer for this API.
              postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SAFE_LITE_PAYLOAD_FAIL_ERROR);
              // To prevent users from attempting to go back and reach the submit flow in this scenario,
              // we'll navigate them to the glass-call-us page
              navigate('/glass-call-us');
            }
          } else {
            // If the draft claim delete was not successful, navigate to the glass-call-us page.
            // (QUESTION: Is this right, or do we need to create a different page for this scenario?)
            navigate('/glass-call-us');
          }
        }
      }
      setModalVisibility(false);
    } else if (
      _get(converseResponse, 'errorCode') === CONVERSE_DRAFT_ALREADY_SUBMITTED_CODE &&
      _get(converseResponse, 'claimNumber')
    ) {
      // The draft was already submitted, typically because the customer went back to a question
      // after submitting. Take them to the claim instead of an error page. The API does not rotate
      // the auth token on this response, so the current one is still valid.
      const submittedClaimNumber: string = converseResponse.claimNumber;
      datadogRum.addAction('converse-next-draft-already-submitted', {
        draftClaimNumber,
        claimNumber: submittedClaimNumber,
      });
      if (isThirdParty) {
        navigate('/third-party-confirmation', {
          state: {
            claimNumber: submittedClaimNumber,
          },
        });
      } else {
        await routeToSubmittedClaim(submittedClaimNumber, authKey);
      }
      setModalVisibility(false);
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_CONVERSE_NEXT_ERROR, draftClaimNumber, converseResponse);
      navigate('/error-banner', {
        state: {
          draftClaimNumber,
        },
      });
    }
  }

  async function deleteDraftWithDispatch(response: any): Promise<any> {
    const deleteResponse = await deleteDraft(
      urlKey,
      sessionId,
      response.data.updatedAuthToken,
      _get(state, 'draftClaimNumber', '').replace(/-/g, '')
    );
    if (deleteResponse && !(deleteResponse instanceof Error)) {
      dispatch(updateAuthToken(_get(deleteResponse, 'data.updatedAuthToken', authKey)));
      dispatch(
        deleteDraftClaim({
          claimNumber: _get(state, 'draftClaimNumber', '').replace(/-/g, ''),
        })
      );
    }
    return deleteResponse;
  }

  function getCustomControl() {
    let control = <></>;

    switch (_get(activeQuestion, 'questionType')) {
      case FnolQuestionTypes.SINGLE_SELECT:
        if (formattedAnswers) {
          control = (
            <Grid item xs={12}>
              <SingleSelect
                selectionChangeHandler={handleSingleSelectionChange}
                styleInfo={themeBasedStyleInfo}
                options={formattedAnswers}
                description={activeQuestion.instructionText}
                header={activeQuestion.displayText}
              ></SingleSelect>
            </Grid>
          );
        }
        break;

      case FnolQuestionTypes.MULTI_SELECT:
        if (formattedAnswers) {
          control = (
            <Grid item xs={12}>
              <MultiSelect
                selectionChangeHandler={handleMultiSelectChange}
                styleInfo={checkboxStyleFromTheme}
                options={formattedAnswers}
                description={activeQuestion.instructionText}
                header={activeQuestion.displayText}
              ></MultiSelect>
            </Grid>
          );
        }
        break;

      case FnolQuestionTypes.FREE_FORM: {
        // Use response from activeQuestion if available (e.g., on back navigation),
        // otherwise use selectedAnswer state (for normal typing)
        const prefillValue = _get(activeQuestion, 'answers[0].response', selectedAnswer);
        control = (
          <Grid item xs={12}>
            <FreeForm
              selectionChangeHandler={handleFreeformChange}
              prefillValue={prefillValue}
              questionId={activeQuestion.id}
              styleInfo={checkboxStyleFromTheme}
              description={activeQuestion.instructionText}
              header={activeQuestion.displayText}
            ></FreeForm>
          </Grid>
        );
        break;
      }

      case FnolQuestionTypes.VEHICLE_DAMAGE_PICKER: {
        // Derive prefill data from activeQuestion if available (on back navigation)
        const damagePickerPrefill = _get(activeQuestion, 'answers[0].answerData')
          ? {
              damageDescription: _get(activeQuestion, 'answers[0].answerData.vehDamageDamageDescription', ''),
              selectedDamageTypeOption: _get(activeQuestion, 'answers[0].answerData.damageType', ''),
              selectedDamageCodes: _get(activeQuestion, 'answers[0].answerData.vehicleDamages', ''),
            }
          : prefillComplexValue;
        control = (
          <Grid item xs={12}>
            <DamagePickerForm
              preFilledDamagePickerData={damagePickerPrefill}
              handleDamagePickerDataUpdate={handleDamagePickerDataUpdate}
            ></DamagePickerForm>
          </Grid>
        );
        break;
      }

      case FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM: {
        // Derive prefill data from activeQuestion if available (on back navigation)
        const thirdPartyInfo = _get(activeQuestion, 'answers[0].answerData.thirdPartyContactForm', []);
        const thirdPartyPrefill =
          thirdPartyInfo.length > 0
            ? thirdPartyInfo.map((thirdParty: any, index: number) => {
                return {
                  id: index,
                  firstName: _get(thirdParty, 'claimContact.contact.firstName', ''),
                  lastName: _get(thirdParty, 'claimContact.contact.lastName', ''),
                  personInjured: _get(thirdParty, 'isThirdPartyInjured', '').length ? true : false,
                  phoneNumber:
                    _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.area', '') +
                    ' ' +
                    _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.number', ''),
                  phoneNumberValid: true,
                  emailAddress: _get(thirdParty, 'claimContact.contact.newPrimaryPhoneEmail.emailAddress', ''),
                  emailAddressValid: true,
                  vehicleInvolved: !!_get(thirdParty, 'vehicleDetail'),
                  vehicleYear: _get(thirdParty, 'vehicleDetail.vehTypeYear', ''),
                  vehicleMake: _get(thirdParty, 'vehicleDetail.vehTypeMake', ''),
                  vehicleModel: _get(thirdParty, 'vehicleDetail.vehTypeModel', ''),
                  addressLine1: _get(thirdParty, 'claimContact.contact.newAddress[0].addressLine1', ''),
                  addressLine2: _get(thirdParty, 'claimContact.contact.newAddress[0].addressLine2', ''),
                  city: _get(thirdParty, 'claimContact.contact.newAddress[0].city', ''),
                  state: _get(thirdParty, 'claimContact.contact.newAddress[0].state', ''),
                  zipCode: _get(thirdParty, 'claimContact.contact.newAddress[0].zip', ''),
                };
              })
            : prefillComplexValue;
        control = (
          <Grid item xs={12}>
            <ThirdPartyInfo
              prefilledThirdPartyContactInfo={thirdPartyPrefill}
              handleThirdPartyContactChange={handleThirdPartyContactChange}
            ></ThirdPartyInfo>
          </Grid>
        );
        break;
      }

      case FnolQuestionTypes.INSURED_CONTACT_TYPE: {
        // Derive prefill data from activeQuestion if available (on back navigation)
        const contactTypePrefill = _get(activeQuestion, 'answers[0].answerData')
          ? {
              phoneNumber:
                _get(activeQuestion, 'answers[0].answerData.areaCode', '') +
                _get(activeQuestion, 'answers[0].answerData.phoneNumber', ''),
              emailAddress: _get(activeQuestion, 'answers[0].answerData.email', ''),
            }
          : prefillComplexValue;
        control = (
          <Grid item xs={12}>
            <ContactInfo
              prefilledContactTypeInfo={contactTypePrefill}
              selectionChangeHandler={handleContactTypeChange}
              styleInfo={checkboxStyleFromTheme}
              description={activeQuestion.instructionText}
              header={activeQuestion.displayText}
            ></ContactInfo>
          </Grid>
        );
        break;
      }
      case FnolQuestionTypes.CLAIMANT_CONTACT_TYPE:
        control = (
          <Grid item xs={12}>
            <ThirdPartyFNOLContact sendClaimantInfo={handleClaimantChange}></ThirdPartyFNOLContact>
          </Grid>
        );
        break;

      case FnolQuestionTypes.END:
        control = (
          <Grid item xs={12}>
            <ReviewFnol
              claimInfo={claimInfo}
              reviewSheet={reviewSheet}
              isAuto={isAuto}
              isThirdParty={isThirdParty}
              handleEdit={handleEdit}
            ></ReviewFnol>
          </Grid>
        );
        break;
    }
    return control;
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      {showErrorBanner && <ErrorBanner></ErrorBanner>}
      <Grid sx={{ marginBottom: '2em' }} item xs={12}>
        <ProgressBar
          currentAnswered={currentAnswered}
          currentStepName={_get(activeQuestion, 'questionType')}
        ></ProgressBar>
      </Grid>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      {getCustomControl()}
      <Grid item xs={12}>
        <CallToAction
          nextButton={true}
          nextButtonDisabled={!nextEnabled || isSubmitting}
          nextButtonContent={
            _get(activeQuestion, 'questionType') === FnolQuestionTypes.END ? submitClaimBtn : continueBtn
          }
          handleNext={handleNext}
          backButton={
            _get(activeQuestion, 'questionType') !== FnolQuestionTypes.END && _get(activeQuestion, 'id') !== 'Q1'
          }
          backButtonContent={backBtn}
          handleBack={handleBack}
          skipButton={false}
          cancelButton={true}
          cancelButtonContent={cancelBtn}
          handleCancel={handleCancel}
        ></CallToAction>
      </Grid>
    </Grid>
  );
}
